// Self-hosted messaging WebSocket client: presence + live conversation events.
// Same-origin connection (auth via the session cookie), auto-reconnect, heartbeat.

let ws = null;
let reconnectTimer = null;
let pingTimer = null;
const handlers = new Set();
let lastViewing = null;               // re-announce viewing context on reconnect
let lastPresence = {};                // cached presence snapshot (user_id -> {name,status,viewing})
const outbox = [];                    // frames queued while the socket isn't OPEN yet

// Send now if OPEN, otherwise queue and flush on open — prevents dropped
// frames (e.g. coedit_join / follow) fired right after connectMessaging().
function rawSend(obj) {
  const s = JSON.stringify(obj);
  if (ws && ws.readyState === WebSocket.OPEN) {
    try { ws.send(s); } catch { outbox.push(s); }
  } else {
    outbox.push(s);
  }
}

function emit(evt) {
  if (evt && evt.type === 'presence') lastPresence = evt.presence || {};
  handlers.forEach((h) => { try { h(evt); } catch { /* ignore */ } });
}

export function onMessagingEvent(handler) {
  handlers.add(handler);
  return () => handlers.delete(handler);
}

export function getPresence() { return lastPresence; }

export function connectMessaging() {
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const url = `${protocol}//${window.location.host}/api/messaging/ws`;
  try {
    ws = new WebSocket(url);
  } catch { return; }

  ws.onopen = () => {
    emit({ type: 'open' });
    clearInterval(pingTimer);
    pingTimer = setInterval(() => rawSend({ type: 'ping' }), 45000);
    rawSend({ type: 'presence' });
    if (lastViewing) rawSend({ type: 'viewing', context: lastViewing });
    // flush anything queued before the socket opened
    while (outbox.length) {
      const s = outbox.shift();
      try { ws.send(s); } catch { /* */ }
    }
  };
  ws.onmessage = (e) => {
    try { emit(JSON.parse(e.data)); } catch { /* */ }
  };
  ws.onclose = () => {
    clearInterval(pingTimer);
    emit({ type: 'close' });
    clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(connectMessaging, 4000);
  };
  ws.onerror = () => { try { ws.close(); } catch { /* */ } };
}

export function sendTyping(conversationId, typing) {
  rawSend({ type: 'typing', conversation_id: conversationId, typing });
}

export function pingPresence() {
  rawSend({ type: 'presence' });
}

// Announce what object/route I'm currently viewing (for "N viewing now" + follow sync).
export function setViewing(context) {
  lastViewing = context || null;
  rawSend({ type: 'viewing', context });
}

// Leader broadcasts navigation/context to followers (context only — never source data).
export function broadcastNav(context) {
  rawSend({ type: 'nav', context });
}

// Leader's live cursor (per-mille coords) → their followers only.
export function broadcastFollowCursor(x, y) {
  rawSend({ type: 'follow_cursor', x, y });
}

export function startFollow(leaderId) {
  rawSend({ type: 'follow', leader_id: leaderId });
}

export function stopFollow(leaderId) {
  rawSend({ type: 'unfollow', leader_id: leaderId });
}

// ── Co-edit (Phase 3): room join/leave, single-writer snapshot sync,
// shared cursors, and edit-control request/grant/take. ──
export function coeditJoin(roomId) { rawSend({ type: 'coedit_join', room_id: roomId }); }
export function coeditLeave(roomId) { rawSend({ type: 'coedit_leave', room_id: roomId }); }
export function coeditOp(roomId, snapshot) { rawSend({ type: 'coedit_op', room_id: roomId, snapshot }); }
export function coeditCursor(roomId, x, y) { rawSend({ type: 'coedit_cursor', room_id: roomId, x, y }); }
export function coeditRequestControl(roomId) { rawSend({ type: 'coedit_request_control', room_id: roomId }); }
export function coeditSetEditor(roomId, userId) { rawSend({ type: 'coedit_set_editor', room_id: roomId, user_id: userId }); }

// ── Live co-browse sessions (host + up to 5 guests): presence, colored
// cursors, follow-the-host navigation. ──
export function cobrowseStart(sessionId) { rawSend({ type: 'cobrowse_start', session_id: sessionId }); }
export function cobrowseJoin(sessionId) { rawSend({ type: 'cobrowse_join', session_id: sessionId }); }
export function cobrowseLeave(sessionId) { rawSend({ type: 'cobrowse_leave', session_id: sessionId }); }
export function cobrowseEnd(sessionId) { rawSend({ type: 'cobrowse_end', session_id: sessionId }); }
export function cobrowseInvite(sessionId, userIds) { rawSend({ type: 'cobrowse_invite', session_id: sessionId, user_ids: userIds }); }
export function cobrowseCursor(sessionId, x, y) { rawSend({ type: 'cobrowse_cursor', session_id: sessionId, x, y }); }
export function cobrowseNav(sessionId, context) { rawSend({ type: 'cobrowse_nav', session_id: sessionId, context }); }
export function cobrowseRR(sessionId, event) { rawSend({ type: 'cobrowse_rr', session_id: sessionId, event }); }
export function cobrowseRRRequest(sessionId) { rawSend({ type: 'cobrowse_rr_request', session_id: sessionId }); }
export function cobrowseResume(sessionId) { rawSend({ type: 'cobrowse_resume', session_id: sessionId }); }
export function cobrowseSetController(sessionId, userId) { rawSend({ type: 'cobrowse_set_controller', session_id: sessionId, user_id: userId }); }
export function cobrowseInput(sessionId, input) { rawSend({ type: 'cobrowse_input', session_id: sessionId, input }); }

export function disconnectMessaging() {
  clearTimeout(reconnectTimer);
  clearInterval(pingTimer);
  try { ws?.close(); } catch { /* */ }
  ws = null;
}
